import {Parameter} from 'platform/components';

import i18n from '@dms/i18n';
import {SnippetProps, useBranches} from '@dms/shared';

import {buildArray} from 'shared/web';

import {RecordAuditBranchSnippetTitle} from './RecordAuditBranchSnippetTitle';

interface UseRecordAuditBranchSnippetArgs {
  branchId: string;
  /** Authoritative branch name when available (detail has it from the audit snapshot). */
  branchName?: string | null;
  isEditable: boolean;
  /** Skeletons the branch name while a just-picked branch hasn't been persisted/refetched yet. */
  isLoading?: boolean;
  onSelect: (branchId: string) => void;
  'data-testid'?: string;
}

/**
 * Builds the branch snippet (name + inline branch switcher + company/street/city parameters)
 * shared by the audit detail header and the draft create header.
 */
export function useRecordAuditBranchSnippet(args: UseRecordAuditBranchSnippetArgs): SnippetProps {
  const branchesQuery = useBranches();

  const branchOptions = branchesQuery.branchOptions.map((option) => ({
    value: option.value,
    label: option.label ?? '',
  }));
  const branchInfo = branchesQuery.getBranchById(args.branchId);
  const resolvedName =
    args.branchName ?? branchOptions.find((option) => option.value === args.branchId)?.label;

  const testId = args['data-testid'] ?? 'recordAudit-header-branchSnippet';

  return {
    label: i18n.t('page.recordAudit.create.fields.branch'),
    title: (
      <RecordAuditBranchSnippetTitle
        data-testid={testId}
        branchName={resolvedName}
        isEditable={args.isEditable}
        isLoading={args.isLoading}
        onSelect={args.onSelect}
      />
    ),
    // Drop the parameters while a just-picked branch is being persisted/refetched so the old
    // branch's company/street/city don't linger next to the loading name — they reappear with
    // the refetched branch.
    parameters: args.isLoading
      ? []
      : buildArray<Parameter>()
          .whenNotNil(branchInfo?.companyName)
          .whenNotNil(branchInfo?.street)
          .whenNotNil(branchInfo?.city),
    'data-testid': testId,
  };
}
